import { AttributeType } from "@/pages/CaseDetail/Configuration/Attributes/type";
import { ccwpApi } from "@/services";

export enum SectionMapping {
  creditor = 1,
  claim = 2,
  claimItem = 3,
}

export type CustomAttributeDefinition = {
  name: string;
  nameFrench: string;
  description: string;
  descriptionFrench: string;
  entityType: number;
  attributeType: number;
  isRequired: boolean;
  isDisplayOnCreate: boolean;
  metadataJSON: string;
  picklistDefinitions: any[];
};

export type SectionFieldsData = {
  id?: string;
  name?: string;
  caseId: string;
  sectionDefinitionId: string;
  customAttributeDefinitionId: string | null;
  displayName: string;
  displayNameFrench: string;
  tooltip: null | string;
  tooltipFrench: null | string;
  sortOrder: number;
  isRequired: boolean;
  fieldType?: string;
  customAttributeDefinition?: CustomAttributeDefinition;
};

export type SectionCustomAttributeDefaults = {
  attributeId: string | null;
  attributeName: string;
  attributeFrenchName: string;
  isRequired: boolean;
};

export type FormSetupFormData = {
  name: string;
  nameFrench: string;
  description: string;
  columns: number;
  sortOrder: number;
  mapping: SectionMapping;
  fields: SectionFieldsData[];
};

export type SectionType =
  | string
  | ""
  | "CreditorContactDetails"
  | "AssigneeDetails"
  | "ClaimDetails"
  | "Documentation"
  | "UploadClaim";

export type SectionRequestBody = {
  formDefinitionId?: string;
  sectionId?: string;
  caseId?: string;
  name: string;
  nameFrench: string;
  description: string;
  mapping: SectionMapping;
  columns: number;
  createSectionAttributeFields?: SectionFieldsData[];
  isPredefined?: boolean;
  sectionType?: SectionType;
};

export type SectionSortRequestBody = {
  sectionDefinitionId: string;
  sortOrder: number;
};

export type SectionItemData = {
  id?: string;
  sectionId?: string;
  name: string;
  nameFrench: string;
  description: string;
  mapping: SectionMapping;
  columns: number;
};

export type SectionItemResponse = {
  caseId?: string;
  id: string;
  name: string;
  nameFrench: string;
  description: string;
  sortOrder: number;
  columns: number;
  sectionMapping: SectionMapping;
  isPredefined?: boolean;
  sectionType?: SectionType;
};

export const formSetupApi = ccwpApi.injectEndpoints({
  endpoints: (builder) => ({
    listCustomAttributes: builder.query<
      SectionCustomAttributeDefaults[],
      { caseId: undefined | string; entityType: number }
    >({
      query: ({ caseId, entityType }) => ({
        url: `Section/ListCustomAttributeNamesByCase`,
        method: "GET",
        params: { CaseId: caseId, EntityType: entityType },
        headers: {
          accept: "application/json",
        },
      }),
      transformResponse: (response: SectionCustomAttributeDefaults[]) => {
        const withCustomField: SectionCustomAttributeDefaults[] = [
          {
            attributeId: null,
            attributeName: "Custom Display Field",
            attributeFrenchName: "Custom Display Field",
            isRequired: false,
          },
          ...response,
        ].map((field) => ({
          ...field,
          customAttributeDefinition: {
            name: field.attributeName,
            nameFrench: field.attributeFrenchName || "",
            description: "",
            descriptionFrench: "",
            entityType: 1,
            attributeType: 1,
            isRequired: field.isRequired,
            isDisplayOnCreate: true,
            metadataJSON: '{"Native":"false"}',
            picklistDefinitions: [],
          },
        }));

        return withCustomField;
      },
    }),
    listSections: builder.query({
      query: (formId: undefined | string) => ({
        url: `Section/ListSectionsByForm`,
        method: "GET",
        params: { formId },
        headers: {
          accept: "application/json",
        },
      }),
      transformResponse: (response: SectionItemResponse[]) => {
        const predefined: SectionItemResponse[] = [
          {
            id: "CreditorContactDetails",
            name: "Creditor Contact Details",
            nameFrench: "Creditor Contact Details",
            sectionType: "CreditorContactDetails",
            description: "",
            columns: 2,
            sectionMapping: SectionMapping.creditor,
            sortOrder: 0,
            isPredefined: true,
          },
          {
            id: "AssigneeDetails",
            name: "Assignee Details",
            nameFrench: "Assignee Details",
            sectionType: "AssigneeDetails",
            description: "",
            columns: 2,
            sectionMapping: 1,
            sortOrder: 0,
            isPredefined: true,
          },
          {
            id: "ClaimDetails",
            name: "Claim Details",
            nameFrench: "Claim Details",
            sectionType: "ClaimDetails",
            description: "",
            columns: 2,
            sectionMapping: SectionMapping.claimItem,
            sortOrder: 0,
            isPredefined: true,
          },
          {
            id: "UploadClaim",
            name: "Upload Claim",
            nameFrench: "Upload Claim",
            sectionType: "UploadClaim",
            description: "",
            columns: 2,
            sectionMapping: SectionMapping.claim,
            sortOrder: 0,
            isPredefined: true,
          },
          {
            id: "Documentation",
            name: "Documentation",
            nameFrench: "Documentation",
            sectionType: "Documentation",
            description: "",
            columns: 2,
            sectionMapping: SectionMapping.claim,
            sortOrder: 0,
            isPredefined: true,
          },
        ];
        const withPredefined: SectionItemResponse[] =
          predefined.concat(response);
        return withPredefined;
      },
    }),
    createSection: builder.mutation({
      query: (requestBody: SectionRequestBody) => ({
        url: `Section/CreateSection`,
        method: "POST",
        body: requestBody,
        headers: {
          accept: "application/json",
        },
      }),
    }),
    updateSection: builder.mutation({
      query: (requestBody: SectionRequestBody) => ({
        url: `Section/UpdateSection`,
        method: "PUT",
        body: requestBody,
        headers: {
          accept: "application/json",
        },
      }),
    }),
    deleteSection: builder.mutation({
      query: (sectionId: string) => ({
        url: `Section/DeleteSection`,
        method: "DELETE",
        params: { sectionId },
      }),
    }),
    sortSections: builder.mutation({
      query: (requestBody: SectionSortRequestBody[]) => ({
        url: `Section/UpdateSectionOrder`,
        method: "PUT",
        body: requestBody,
        headers: {
          accept: "application/json",
        },
      }),
    }),
    listSectionAttributes: builder.query<
      SectionCustomAttributeDefaults[],
      string
    >({
      query: (sectionId: string) => ({
        url: `Section/ListSectionAttributesBySection`,
        method: "GET",
        params: { sectionId },
        headers: {
          accept: "application/json",
        },
      }),
      // sort by sortOrder
      transformResponse: (response: SectionCustomAttributeDefaults[]) => {
        const sorted = response.sort(
          (a: any, b: any) => a.sortOrder - b.sortOrder
        );
        return sorted;
      },
    }),
  }),
});

export const mapUpdateSectionRequest = (
  section: SectionItemData,
  fields: SectionFieldsData[]
) => {
  return {
    ...section,
    createSectionAttributeFields: fields,
  };
};

export const mapFormToRequestBody = (
  formData: FormSetupFormData,
  selectedSection: SectionItemData
) => {
  const selectedFields: SectionFieldsData[] = formData?.fields.filter(
    (field: SectionFieldsData) => {
      return field.sectionDefinitionId === selectedSection.id;
    }
  );
  const requestBody: SectionRequestBody = {
    sectionId: selectedSection?.id,
    name: formData.name,
    nameFrench: formData.nameFrench,
    description: formData.description,
    columns: formData?.columns || 1,
    mapping: selectedSection?.mapping,
    createSectionAttributeFields: selectedFields.map(
      (field: SectionFieldsData, index: number) => {
        return {
          // use 00000000-0000-0000-0000-000000000000 for new fields
          // other wise, pass the id of the field
          id: field.id,
          caseId: field.caseId,
          sectionDefinitionId: field.sectionDefinitionId,
          customAttributeDefinitionId: field.customAttributeDefinitionId,
          displayName: field.displayName,
          displayNameFrench: field.displayNameFrench,
          tooltip: field.tooltip,
          tooltipFrench: field.tooltipFrench,
          sortOrder: index,
          isRequired: field.isRequired,
          ...(!field.customAttributeDefinitionId && {
          customAttributeDefinition: {
            name: field.displayName,
            nameFrench: field.displayNameFrench,
            description: field.tooltip || "",
            descriptionFrench: field.tooltipFrench || "",
            entityType: selectedSection.mapping,
            attributeType: AttributeType[field.fieldType as keyof typeof AttributeType] || AttributeType.FreeText,
            isRequired: field.isRequired,
            isDisplayOnCreate: true,
            metadataJSON: field.customAttributeDefinition?.metadataJSON || "{\"Native\":\"false\"}",
            picklistDefinitions: field.customAttributeDefinition?.picklistDefinitions || [],
          },
        }),
        };
      }
    ),
  };
  return requestBody;
};


export const {
  useListCustomAttributesQuery,
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useListSectionsQuery,
  useSortSectionsMutation,
  useDeleteSectionMutation,
  useListSectionAttributesQuery,
} = formSetupApi;
